import React from 'react';
import { format, parseISO } from 'date-fns';
import { ButtonText } from '@saucelabs/ds-button';
import { Link } from '@saucelabs/ds-link';
import { Typography } from '@saucelabs/ds-typography';
import { Col, Row } from '@saucelabs/sl-component-grid';
import { IconAutomatedOutline } from '@saucelabs/ds-icons';
import { InlineAlert } from '@saucelabs/ds-alert';
import { useFlags } from '@saucelabs/sl-util-launchdarkly';

import {
  CREDITS_PER_BLOCK,
  EMAIL_SUPPORT_PLAN_UPDATE,
  NEXT_BILL_DATE_FORMAT,
  ROUTE_EMAIL_SUPPORT,
  ROUTE_PARENT_SECTION,
  ROUTE_SECTION_BILLING_INFORMATION,
  ROUTE_SECTION_PURCHASE,
  SUBSCRIPTION_STATE,
  USAGE_TYPE,
} from '../../constants';
import type { Subscription } from '../../records/Subscription';
import { Title } from './Title';
import { Card } from '../Layout/Card';
import CreditCardReadOnly from '../CreditCardReadOnly';
import { trackEvent, EVENTS } from '../../tracking';
import { amountSeparator, formatThousand } from '../../utils/numbers';
import Styles from './SummaryTile.styles';
import { CreditsTopupColumn } from './CreditsTopupColumn';

export enum SubscriptionUsageTileMode {
  DISPLAY_SUBSCRIPTIONS = 'DisplaySubscriptions',
  CANCEL_SUBSCRIPTIONS = 'CancelSubscriptions',
}

interface CoreProps {
  creditCardInfo: CreditCardInfo,
  subscription: Subscription
}

interface CancellingProps extends CoreProps {
  mode: SubscriptionUsageTileMode.CANCEL_SUBSCRIPTIONS
  fetchAutoTopup: never
  isAutoTopupEnabled: never
  updateAutoTopup: never
}

interface DisplayingProps extends CoreProps {
  mode: SubscriptionUsageTileMode.DISPLAY_SUBSCRIPTIONS
  fetchAutoTopup?: () => void
  isAutoTopupEnabled?: boolean
  updateAutoTopup?: (p: boolean) => void
}

type Props = CancellingProps | DisplayingProps

const trackUpdatePlanEvent = (subscription: Subscription) => {
  const payload = {
    product: USAGE_TYPE,
    usage_credits: subscription.getCreditBlocksAmount(),
  };

  trackEvent(
    EVENTS.PLANS_CLICK_UPDATE_BUTTON,
    payload,
  );
};

export const getUpdateUrl = (url: string): string => {
  const urlParams = new URLSearchParams({ action: EMAIL_SUPPORT_PLAN_UPDATE });

  return `${url}?${urlParams.toString()}`;
};

const SubscriptionUsageTile: React.FC<Props> = ({
  children,
  creditCardInfo,
  mode,
  subscription,
  fetchAutoTopup,
  isAutoTopupEnabled,
  updateAutoTopup,
}) => {
  const flags = useFlags();
  const trackUpdatePlan = React.useCallback(
    () => trackUpdatePlanEvent(subscription),
    [subscription]);
  const isCancelling = mode === SubscriptionUsageTileMode.CANCEL_SUBSCRIPTIONS;
  const borderClass = !isCancelling ? 'border-2 border-blue-400 shadow-sm' : 'border-1 border-red-900 shadow-none';
  const nextBillDateLabel = isCancelling ? 'Subscription till' : 'Next bill day -';
  React.useEffect(() => {
    if (!isCancelling) {
      if (!fetchAutoTopup) throw new Error('fetchAutoTopup was not passed to this component when it should have been');
      else fetchAutoTopup();
    }
  }, []);

  const colWidth = isCancelling ? 6 : 4;

  return (
    <Card
      className={`p-3 border-solid ${borderClass}`}
    >
      <Row between="xs">
        <Col xs={12}>
          <Title
            icon={IconAutomatedOutline}
            isLegacy={false}
            name={subscription.displayName()}
          />
        </Col>
      </Row>
      <Row className="mt-1">
        <Col xs={colWidth}>
          <div data-qa="summary-tile-plan-cost">
            <Typography
              className="font-semibold text-high-emphasis"
              component="span"
              variant="body-large"
            >
              {amountSeparator(
                subscription.plan.calculatePeriodRate(subscription.getCreditBlocksAmount()),
                'standard',
              )}
            </Typography>
            <Typography
              className="text-high-emphasis"
              component="span"
              variant="body-large"
            >
              {` / ${subscription.isMonthly() ? 'month' : 'year'} `}
            </Typography>
            <Typography
              className="text-medium-emphasis"
              component="span"
              variant="body-large"
            >
              {`(${subscription.isMonthly() ? 'Monthly' : 'Annual'} Plan)`}
            </Typography>
          </div>
          <div
            className="mt-1"
            data-qa="summary-tile-credit-amount"
          >
            <Typography
              className="font-semibold text-high-emphasis"
              component="span"
              variant="body"
            >
              {`${formatThousand(
                subscription.plan.creditsPerPeriod(subscription.getCreditBlocksAmount()),
                'standard',
              )} credits`}
            </Typography>
            <Typography
              className="text-high-emphasis"
              component="span"
              variant="body"
            >
              {` / ${subscription.isMonthly() ? 'month' : 'year'}`}
            </Typography>
          </div>
          <div
            className="mt-1"
            data-qa="summary-tile-started-on"
          >
            <Typography
              className="text-medium-emphasis text-gray-600"
              variant="body"
            >
              {'Started on '}
              {subscription.displayStartedOnDate()}
            </Typography>
          </div>
          {!isCancelling && (
            <ButtonText
              className="mt-2"
              color="primary"
              onPress={trackUpdatePlan}
              size="md"
              to={`/${ROUTE_PARENT_SECTION}/${ROUTE_SECTION_PURCHASE}`}
              type="button"
              variant="outline"
            >
              Update Plan
            </ButtonText>
          )}
        </Col>
        <Col
          className={Styles['plan-info-column']}
          xs={colWidth}
        >
          <div className="pl-3">
            {!isCancelling && (
              <>
                <Typography
                  className="text-high-emphasis font-semibold"
                  variant="body-large"
                >
                  Payment Method
                </Typography>
                <div className="mt-1">
                  <CreditCardReadOnly creditCardInfo={creditCardInfo} />
                </div>
              </>
            )}
            <div className="mt-1">
              <Typography
                className="text-medium-emphasis text-gray-600"
                data-qa="summary-tile-next-bill-date"
                variant="body"
              >
                {`${nextBillDateLabel} `}
                {subscription.displayNextBillDate()}
              </Typography>
            </div>
            {!isCancelling && (
              <div className="mt-2">
                <Link
                  emphasize
                  to={`/${ROUTE_PARENT_SECTION}/${ROUTE_SECTION_BILLING_INFORMATION}`}
                >
                  Manage Billing Information
                </Link>
              </div>
            )}
          </div>
        </Col>
        {!isCancelling && updateAutoTopup && (
          <Col
            className={Styles['plan-info-column']}
            xs={colWidth}
          >
            <CreditsTopupColumn
              topupEnabled={isAutoTopupEnabled}
              updateAutoTopup={updateAutoTopup}
            />
          </Col>
        )}
      </Row>
      {subscription.pending_change?.start_date && (
        <InlineAlert
          className="mt-3"
          color="warning"
          hideIcon
        >
          <Typography
            className="font-semibold"
            disableSpacing
            variant="body-large"
          >
            {`The changes you made to your plan will take effect on ${format(subscription.getPendingChangeDate(), NEXT_BILL_DATE_FORMAT)}`}
          </Typography>
          <Typography
            disableSpacing
            variant="body-large"
          >
            {'Your plan will include '}
            {formatThousand(subscription.getPendingChangeCredits(), 'standard')}
            {` credits/${subscription.pending_change?.plan?.interval_unit}.`}
          </Typography>
        </InlineAlert>
      )}
      {subscription.state === SUBSCRIPTION_STATE.CANCELED && (
        <InlineAlert
          className="mt-3"
          color="warning"
          hideIcon
        >
          <Typography
            className="font-semibold"
            disableSpacing
            variant="body-large"
          >
            The changes you made to your plan will take effect on
            {' '}
            {subscription.displayNextBillDate()}
          </Typography>
          <Typography
            className="font-normal"
            disableSpacing
            variant="body-large"
          >
            {`${subscription.displayName()} plan will be canceled.`}
          </Typography>
        </InlineAlert>
      )}
      {children}
    </Card>
  );
};

export default SubscriptionUsageTile;
export {
  SubscriptionUsageTile,
};
